
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface AdminCheckWrapperProps {
  children: (isAdmin: boolean) => React.ReactNode;
}

const AdminCheckWrapper = ({ children }: AdminCheckWrapperProps) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if the user is an admin
  useEffect(() => {
    const checkUserRole = async () => {
      if (!user) {
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }
      
      try {
        const { data, error } = await supabase.rpc('has_role', { _role: 'admin' });
        
        if (error) {
          console.error('Error checking admin role:', error);
          setIsAdmin(false);
        } else {
          setIsAdmin(data === true);
        }
      } catch (error) {
        console.error('Error in role check:', error);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkUserRole();
  }, [user]);

  // If not an admin, redirect to home page
  useEffect(() => {
    if (!isLoading && user && !isAdmin) {
      toast.error("You don't have permission to access this page");
      window.location.href = '/';
    }
  }, [user, isAdmin, isLoading]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center">
          <div className="h-8 w-8 border-4 border-primary/50 border-t-primary rounded-full animate-spin mb-4"></div>
          <p className="text-muted-foreground">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Not logged in or not admin
  if (!user || !isAdmin) {
    return null;
  }

  // User is admin, render children
  return <>{children(isAdmin)}</>;
};

export default AdminCheckWrapper;
